/**
 * Inline notification rendered when the agent calls `save_memory`.
 *
 * Unlike `MemoryProposalCard`, the memory has already been written to the
 * store by the time this component renders — the agent's `save_memory`
 * tool returned the persisted memory id. The notification's job is to
 * keep the user aware of the write and offer an undo affordance, so the
 * silent-save UX doesn't feel like things are happening invisibly.
 *
 * Three states:
 *   - saved (initial)   — shows "Saved: <text>" + Undo button
 *   - undone            — shows "Removed: <text>"; collapses to a one-liner
 *   - error             — undo failed; shows the error and a Try-again button
 */

import { Box, Button, Chip, Paper, Stack, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';

import { memoryKeys } from '../api/memoryKeys';
import { useForgetMemory } from '../api/memoryQueries';
import { memorySavedSchema, type MemorySaved } from '../api/memoryTypes';

/**
 * Same envelope-stripping logic as MemoryProposalCard. The agent returns
 * `save_memory` as a JSON string for the same Strands-Python-repr reason;
 * unwrap then parse.
 */
const unwrapToolResult = (raw: unknown): unknown => {
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    const text = (raw as Record<string, unknown>).text;
    if (typeof text === 'string') {
      try {
        return JSON.parse(text);
      } catch {
        return raw;
      }
    }
    return raw;
  }
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw);
    } catch {
      return raw;
    }
  }
  return raw;
};

interface MemorySavedNotificationProps {
  /** Raw `result` payload from the agent's tool call. Validated here. */
  savedResult: unknown;
  /** Chat session id, threaded to the forget call for session-scope saves. */
  sessionId?: string | null;
  disabled?: boolean;
}

type CardState = { kind: 'saved' } | { kind: 'undone' } | { kind: 'error'; message: string };

const SCOPE_LABEL: Record<MemorySaved['scope'], string> = {
  session: 'this session',
  user: 'me',
};

export const MemorySavedNotification = ({
  savedResult,
  sessionId,
  disabled,
}: MemorySavedNotificationProps) => {
  const saved = useMemo<MemorySaved | null>(() => {
    const candidate = unwrapToolResult(savedResult);
    const parsed = memorySavedSchema.safeParse(candidate);
    return parsed.success ? parsed.data : null;
  }, [savedResult]);

  const [state, setState] = useState<CardState>({ kind: 'saved' });
  const forgetMutation = useForgetMemory();
  const queryClient = useQueryClient();

  // Invalidate the memory list cache so the sidecar panel reflects the
  // newly-saved entry. The agent's save_memory bypassed the frontend's
  // mutation hook (it ran server-side via the Strands tool path), so
  // TanStack Query's auto-invalidation didn't fire — without this, the
  // panel keeps serving the cached "empty list" from before the save.
  // Keyed on `saved?.id` so we invalidate once per distinct save and
  // don't loop on re-renders.
  useEffect(() => {
    if (!saved) return;
    queryClient.invalidateQueries({ queryKey: memoryKeys.all });
  }, [saved?.id, queryClient]);

  // Replay-recovery used to flip the notification to "Removed" when the
  // memory id was missing from the user's memory list. That logic
  // mis-fires on fresh saves: at mount the list cache is the pre-save
  // snapshot (id not in it yet), so the component renders "Removed" for
  // a memory that was just successfully written. Trusting local state
  // (explicit Undo click → 'undone'; otherwise 'saved') drops a rare
  // session-replay edge case (user removed the memory from the sidecar
  // and is now viewing chat history) but eliminates the more harmful
  // bug. If the user does click Undo on an already-deleted memory, the
  // backend's MemoryNotFoundError surfaces in the existing error state.

  if (!saved) {
    // Tool call returned an error envelope (e.g. blank text, scope=org
    // rejected). The underlying ToolCallCard surfaces the error already;
    // nothing to add here.
    return null;
  }

  const submitUndo = () => {
    forgetMutation.mutate(
      {
        memoryId: saved.id,
        sessionId: saved.scope === 'session' ? sessionId ?? undefined : undefined,
      },
      {
        onSuccess: () => setState({ kind: 'undone' }),
        onError: (err) =>
          setState({
            kind: 'error',
            message: err instanceof Error ? err.message : 'failed to undo memory',
          }),
      }
    );
  };

  const submitting = forgetMutation.isPending;
  const isUndone = state.kind === 'undone';

  if (isUndone) {
    return (
      <Paper
        variant="outlined"
        sx={{ padding: 1, borderRadius: 1, backgroundColor: 'action.hover' }}
        data-testid="memory-saved-notification-undone"
      >
        <Typography variant="caption" color="text.secondary">
          Removed: "{saved.text}"
        </Typography>
      </Paper>
    );
  }

  if (state.kind === 'error') {
    return (
      <Paper
        variant="outlined"
        sx={{ padding: 1.25, borderRadius: 1, borderColor: 'error.main' }}
        data-testid="memory-saved-notification-error"
      >
        <Typography variant="caption" color="error.main" display="block">
          Could not undo: {state.message}
        </Typography>
        <Button size="small" onClick={() => setState({ kind: 'saved' })}>
          Try again
        </Button>
      </Paper>
    );
  }

  // saved
  return (
    <Paper
      variant="outlined"
      sx={{ padding: 1, borderRadius: 1, backgroundColor: 'action.hover' }}
      data-testid="memory-saved-notification"
    >
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography variant="caption" color="text.secondary">
          Saved to memory ({SCOPE_LABEL[saved.scope]})
        </Typography>
        <Chip size="small" variant="outlined" label={saved.kind} />
        <Box sx={{ marginLeft: 'auto' }}>
          <Button
            size="small"
            variant="text"
            disabled={disabled || submitting}
            onClick={submitUndo}
            data-testid="memory-saved-undo"
          >
            Undo
          </Button>
        </Box>
      </Stack>
      <Typography variant="body2" sx={{ marginTop: 0.5, fontStyle: 'italic' }}>
        "{saved.text}"
      </Typography>
    </Paper>
  );
};
